import type { ReactNode } from 'react';
import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';

import { cn } from '@rocco/utils/cn';

/** `useLayoutEffect` warns on the server; the measurement is browser-only. */
const useIsomorphicLayoutEffect =
    typeof document === 'undefined' ? useEffect : useLayoutEffect;

/** How long the pane takes to cross and finish wobbling. */
const TRAVEL_MS = 560;

/**
 * The deformation, as a body of liquid rather than a rectangle being scaled.
 *
 * Four things happen, in this order, and all four are needed:
 *
 *  1. It swells. Both axes grow — a droplet under surface tension rounds out
 *     as it releases, and a pane that only stretches along its path reads as
 *     a rubber band instead. This is the part that makes it look like matter.
 *  2. It stretches, hardest at a quarter through, when it is travelling
 *     fastest. Widest and tallest at the same moment, so it reads as swelling
 *     while it runs rather than being pulled thin.
 *  3. It overshoots and inverts. Past the middle it is narrower than it
 *     started and taller still — the trailing edge catching up compresses it
 *     along its path and pushes the volume out sideways.
 *  4. It rings down. One smaller opposite wobble, then rest. A single
 *     correction reads as a mistake being fixed; two decaying ones read as
 *     something settling.
 *
 * Written as explicit keyframes and played through the Web Animations API
 * rather than as a CSS transition: a transition can only interpolate between
 * two states, so the swell, the inversion and the ring-down would each need
 * their own timer and would drift apart from the travel underneath them.
 */
const DEFORM: Keyframe[] = [
    { transform: 'scale(1, 1)', offset: 0 },
    { transform: 'scale(1.08, 1.06)', offset: 0.1 },
    {
        transform: 'scale(1.2, 1.12)',
        offset: 0.26,
        easing: 'cubic-bezier(0.3, 0, 0.2, 1)',
    },
    { transform: 'scale(0.93, 1.1)', offset: 0.55 },
    { transform: 'scale(1.05, 0.97)', offset: 0.76 },
    { transform: 'scale(0.99, 1.01)', offset: 0.9 },
    { transform: 'scale(1, 1)', offset: 1 },
];

/**
 * The dark-ground lens: a light pane on a near-black page.
 *
 * A lit top edge, a darker bottom one, and the asymmetry between them is what
 * stops it reading as a flat white rectangle. Written as the property rather
 * than `shadow-*`, which funnels a single layer through `--tw-shadow` and
 * drops the rest of a stack.
 */
const LENS_DARK =
    '[box-shadow:inset_0_1.5px_0_0_rgba(255,255,255,0.95),inset_0_-3px_7px_-2px_rgba(0,0,0,0.28),inset_0_0_0_1px_rgba(255,255,255,0.35),0_8px_20px_-8px_rgba(0,0,0,0.55)]';

/**
 * The light-ground lens: white, lifted off the track.
 *
 * The dark-ground pane works by catching light along its rim. On a light page
 * there is no light to catch and no contrast to spend, so the pane is white
 * and the shadow under it does all the work — the selection reads as a card
 * sitting on the track rather than as a hole cut through it.
 */
const LENS_LIGHT =
    '[box-shadow:0_1px_2px_0_rgba(0,0,0,0.06),0_6px_16px_-8px_rgba(0,0,0,0.22)]';

const TONES = {
    dark: {
        track: 'border border-white/15 bg-white/5 backdrop-blur-[6px]',
        lens: cn(
            'bg-white/90 backdrop-blur-[10px] backdrop-saturate-150',
            LENS_DARK,
        ),
    },
    light: {
        // No frame at all: no border, no fill. On a light page the track was
        // drawing a box round a row of words that reads perfectly well without
        // one — the travelling pane behind the current label is the only mark
        // the set needs.
        track: 'bg-transparent',
        lens: cn('bg-white', LENS_LIGHT),
    },
} as const;

export interface LensSwitchItem {
    key: string;
    /** The item's own control — a button, a link, whatever it needs to be. */
    render: (isActive: boolean) => ReactNode;
}

/**
 * A set of controls sharing one rounded track, with the selection as a single
 * pane of glass that travels between them.
 *
 * One object moving is legible in a way independent colour changes are not,
 * and it is what makes a set read as a switch rather than as loose buttons. It
 * squashes and stretches on the way: a pane that translates at constant size
 * reads as a sprite being repositioned, and giving it mass is the whole point
 * of the material.
 *
 * Everything is measured from the items themselves, so the labels may be any
 * length and any number.
 */
export const LensSwitch = ({
    items,
    activeKey,
    tone = 'dark',
    ariaLabel,
    className,
}: {
    items: LensSwitchItem[];
    activeKey: string;
    tone?: keyof typeof TONES;
    ariaLabel: string;
    className?: string;
}) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const lensRef = useRef<HTMLSpanElement>(null);
    const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const [lens, setLens] = useState<{ left: number; width: number } | null>(
        null,
    );

    const measure = useCallback(() => {
        const active = itemRefs.current[activeKey];
        if (!active || !trackRef.current) return;
        setLens({ left: active.offsetLeft, width: active.offsetWidth });
    }, [activeKey]);

    // Before paint, so the pane is never drawn at the wrong place first.
    useIsomorphicLayoutEffect(measure, [measure, items.length]);

    // The labels are type, so their widths move with the font once it loads
    // and with the fluid root size on resize.
    useEffect(() => {
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [measure]);

    // Only a change of selection deforms it — the first measurement places the
    // pane, and placing it should not look like a move.
    const placed = useRef(false);
    const running = useRef<Animation | null>(null);
    useEffect(() => {
        if (!placed.current) {
            placed.current = true;
            return;
        }
        const node = lensRef.current;
        if (!node?.animate) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }
        // A second press mid-flight replaces the wobble rather than layering a
        // new one over it, which would compound the scales.
        running.current?.cancel();
        running.current = node.animate(DEFORM, {
            duration: TRAVEL_MS,
            easing: 'linear',
        });
    }, [activeKey]);

    const skin = TONES[tone];

    return (
        <div
            ref={trackRef}
            role="tablist"
            aria-label={ariaLabel}
            className={cn(
                'relative flex w-fit items-center gap-1 rounded-[1.375rem] p-1',
                skin.track,
                className,
            )}
        >
            {lens ? (
                <span
                    ref={lensRef}
                    aria-hidden="true"
                    className={cn(
                        'pointer-events-none absolute top-1 bottom-1 rounded-[1rem]',
                        skin.lens,
                    )}
                    style={{
                        left: lens.left,
                        width: lens.width,
                        // The travel underneath the deformation. Overshoots so
                        // the pane arrives with momentum rather than
                        // decelerating into place.
                        transition: `left ${TRAVEL_MS}ms cubic-bezier(0.34, 1.36, 0.36, 1), width ${TRAVEL_MS}ms cubic-bezier(0.34, 1.36, 0.36, 1)`,
                    }}
                />
            ) : null}

            {items.map((item) => (
                <div
                    key={item.key}
                    ref={(node) => {
                        itemRefs.current[item.key] = node;
                    }}
                    // Above the pane, and transparent — the glass behind is the
                    // selection, so a fill here would cover it.
                    className="relative z-10 shrink-0"
                >
                    {item.render(item.key === activeKey)}
                </div>
            ))}
        </div>
    );
};
